import React, { useCallback, useMemo } from "react";
import { toast } from "react-toastify";

import AddchartIcon from "@mui/icons-material/Addchart";
import DeleteIcon from "@mui/icons-material/Delete";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import EmailIcon from "@mui/icons-material/MailOutline";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { Menu } from "components/popups";
import { useNavigate, useNavigationHooks } from "hooks";
import { useDealActions, usePickUserPermissions, useUserRoleSelector, useViewActions } from "store";
import { UserPermissionType } from "store/types";
import { IDealPreview } from "store/types";
import { copyToClipboard } from "utils";
import { isRoleAnalyst } from "utils/user";

import { RoleName } from "../../../../constants";

interface Props extends IDealPreview {
  onClickDelete?: (id: number, title: string) => void;
}

export const Card: React.FC<Props> = ({ id, title, asset_class, inviter, deal_role, onClickDelete, email }) => {
  const navigate = useNavigate();
  const { setParams } = useNavigationHooks();
  const { setCharacteristicsManageAction } = useDealActions();
  const { setCreateManyMeasureData } = useViewActions();
  const role = useUserRoleSelector();
  const dealMetrics = usePickUserPermissions("deal_metrics");
  const noAccessPermissionMetrics = dealMetrics && dealMetrics === UserPermissionType.NO_ACCESS;
  const isShared = !!inviter || deal_role !== null;

  const dealInfo: string = title + ` ${"<" + (email || "") + ">"}`;

  const onClickCopy = useCallback(() => {
    try {
      copyToClipboard(dealInfo);
      toast("Email address was copied to clipboard");
    } catch (_error) {
      console.log("Something went wrong, impossible to copy deal email");
    }
  }, [dealInfo, title, email]);

  const menuItems = useMemo(() => {
    const items = [
      {
        title: "Characteristics",
        icon: <MoreHorizIcon />,
        onClick: () =>
          setCharacteristicsManageAction({
            dealName: title,
            dealId: id,
            viewOnly: false
          }),
        hidden: !isRoleAnalyst({ role })
      },
      {
        title: "Add value",
        icon: <AddchartIcon />,
        onClick: () => setCreateManyMeasureData({ dealId: id }),
        hidden: !isRoleAnalyst({
          role,
          preventTV: role?.name === RoleName.TeamViewer,
          preventUser: noAccessPermissionMetrics
        })
      },
      {
        title: "Email",
        icon: <EmailIcon />,
        onClick: () => onClickCopy()
      },
      {
        title: "Cashflow and Data",
        icon: <InsertChartOutlinedIcon />,
        onClick: () => {
          navigate({ route: "dealMonitoringData", params: { deal_id: id } });
        },
        hidden: !isRoleAnalyst({ role, preventUser: noAccessPermissionMetrics })
      }
    ];
    if (onClickDelete) {
      items.push({
        title: "Delete deal",
        icon: <DeleteIcon />,
        onClick: () => onClickDelete(id, title)
      });
    }
    return items;
  }, [
    id,
    title,
    onClickDelete,
    role,
    noAccessPermissionMetrics,
    setCharacteristicsManageAction,
    setCreateManyMeasureData,
    setParams,
    navigate,
    onClickCopy
  ]);

  return (
    <Stack
      onClick={() => navigate({ route: "deal", params: { deal_id: id } })}
      sx={{
        bgcolor: "white",
        borderRadius: "8px",
        p: "12px",
        gap: "12px",
        cursor: "pointer",
        position: "relative",
        height: "100%",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        minHeight: "74px",
        boxShadow: "0px 1px 2px 0px #1212171A",
        boxSizing: "border-box"
      }}
    >
      {/* Header with menu */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Stack gap="4px" flexGrow={1} overflow="hidden">
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 500,
              color: "#0a3142",
              lineHeight: 1.2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              color: "#5d7782",
              lineHeight: 1.2
            }}
          >
            {asset_class?.account_alias || asset_class?.presentation_name || "Hedge Fund"}
          </Typography>
        </Stack>
        <Menu
          icon={<MoreVertIcon sx={{ fontSize: "18px", color: "#5d7782" }} />}
          iconButtonProps={{
            sx: {
              width: "24px",
              height: "24px",
              padding: 0
            }
          }}
          options={menuItems}
        />
      </Stack>

      {/* Shared with me tag */}
      {isShared && (
        <Box>
          <Chip
            label="Shared with me"
            size="small"
            sx={{
              height: "20px",
              fontSize: "12px",
              fontWeight: 400,
              bgcolor: "#FBE3D3",
              color: "#B96006",
              "& .MuiChip-label": {
                padding: "0 8px"
              }
            }}
          />
        </Box>
      )}
    </Stack>
  );
};
